
import React, { useState } from "react";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useForm } from "react-hook-form";
import { useToast } from "@/components/ui/use-toast";
import { Loader2, Send, MessageSquare, PhoneCall, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";

type FormData = {
  name: string;
  email: string;
  subject: string;
  message: string;
};

const SupportPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      name: user?.firstName || "",
      email: user?.email || "",
      subject: "",
      message: "",
    }
  });
  
  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    
    try {
      // Here we'd typically send this to a backend
      // For now, just simulate a send and show success message
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Store in Supabase if available
      if (user) {
        // Use type assertion for now until types are updated
        await supabase
          .from('support_tickets' as any)
          .insert([
            {
              user_id: user.id,
              name: data.name,
              email: data.email,
              subject: data.subject,
              message: data.message,
              status: 'new'
            },
          ] as any);
      }
      
      toast({
        title: "Mensagem enviada",
        description: "Sua mensagem foi enviada com sucesso. Responderemos em breve.",
      });
      
      reset();
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        title: "Erro ao enviar",
        description: "Ocorreu um erro ao enviar sua mensagem. Por favor, tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead
        title="Suporte ao Cliente - AI Palpite Guru"
        description="Entre em contato com o suporte do AI Palpite Guru. Atendimento via formulário, WhatsApp e email. Tire suas dúvidas e resolva problemas rapidamente."
        keywords="suporte palpite guru, contato palpite guru, ajuda jogo do bicho, atendimento"
        canonical="/suporte"
      />
    <div className="container mx-auto px-4 py-12">
      <h1 className="text-3xl font-bold text-center mb-8">Suporte ao Cliente</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
        <div className="md:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Entre em Contato</CardTitle>
              <CardDescription>Preencha o formulário abaixo para enviar sua mensagem.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label htmlFor="name" className="text-sm font-medium">
                      Nome
                    </label>
                    <Input
                      id="name"
                      {...register("name", { required: "Nome é obrigatório" })}
                      placeholder="Seu nome"
                    />
                    {errors.name && (
                      <p className="text-red-500 text-xs">{errors.name.message}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-medium">
                      Email
                    </label>
                    <Input
                      id="email"
                      type="email"
                      {...register("email", { 
                        required: "Email é obrigatório",
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: "Email inválido"
                        }
                      })}
                      placeholder="seu.email@exemplo.com"
                    />
                    {errors.email && (
                      <p className="text-red-500 text-xs">{errors.email.message}</p>
                    )}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="subject" className="text-sm font-medium">
                    Assunto
                  </label>
                  <Input
                    id="subject"
                    {...register("subject", { required: "Assunto é obrigatório" })}
                    placeholder="Assunto da sua mensagem"
                  />
                  {errors.subject && (
                    <p className="text-red-500 text-xs">{errors.subject.message}</p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="message" className="text-sm font-medium">
                    Mensagem
                  </label>
                  <Textarea
                    id="message"
                    {...register("message", { required: "Mensagem é obrigatória" })}
                    placeholder="Digite sua mensagem aqui..."
                    rows={6}
                  />
                  {errors.message && (
                    <p className="text-red-500 text-xs">{errors.message.message}</p>
                  )}
                </div>
              </form>
            </CardContent>
            <CardFooter>
              <Button 
                onClick={handleSubmit(onSubmit)}
                disabled={isSubmitting} 
                className="w-full"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Enviar Mensagem
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>
        </div>
        
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Suporte por Chat</CardTitle>
              <CardDescription>Converse com nossa equipe de suporte</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Nosso chat de suporte está disponível nos dias úteis das 9h às 18h.
              </p>
              <Button className="w-full">
                <MessageSquare className="mr-2 h-4 w-4" />
                Iniciar Chat
              </Button>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Outras Formas de Contato</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center">
                <PhoneCall className="h-4 w-4 mr-2 text-muted-foreground" />
                <span>(11) 3456-7890</span>
              </div>
              <div className="flex items-center">
                <Mail className="h-4 w-4 mr-2 text-muted-foreground" />
                <span>suporte@palpiteguru.com</span>
              </div>
              
              <div className="bg-muted/30 p-4 rounded-lg mt-4">
                <h4 className="font-medium mb-2">Horário de Atendimento</h4>
                <p className="text-sm">Segunda a Sexta: 9h às 18h</p>
                <p className="text-sm">Sábado: 9h às 13h</p>
                <p className="text-sm">Domingo: Fechado</p>
              </div>
            </CardContent>
          </Card>
          
          <div className="bg-muted/30 p-4 rounded-lg text-center">
            <a href="/faq" className="text-primary hover:underline">
              Consulte também nossas perguntas frequentes
            </a>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default SupportPage;
